import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { handleCriticalAuthError } from '../utils/authUtils';
import { authLogger } from '../utils/logger';

/**
 * Hook to handle authentication errors throughout the application
 * Provides a consistent way to handle auth errors and force reauthentication
 */
export const useAuthErrorHandler = () => {
  const { forceReauthentication, immediateForceLogin } = useAuth();

  /**
   * Handle any error and check if it requires force reauthentication
   * @param error - The error to handle
   * @param context - Optional context for logging (e.g., 'userProfile', 'chatService')
   * @returns true if reauthentication was forced, false otherwise
   */
  const handleError = useCallback(async (error: any, context?: string): Promise<boolean> => {
    const logContext = context ? `[${context}]` : '';
    authLogger.debug(`${logContext} Handling potential auth error:`, error);

    try {
      // Use the global handler first
      const handled = await handleCriticalAuthError(error);
      if (handled) {
        authLogger.debug(`${logContext} Critical auth error handled globally`);
        return true;
      }

      // Fallback to context-based force reauthentication
      if (error && typeof error === 'object') {
        const errorMessage = error.message || error.errorMessage || '';
        const errorCode = error.errorCode || error.error || '';

        // Additional check for any auth-related errors
        if (errorMessage.toLowerCase().includes('auth') || 
            errorMessage.toLowerCase().includes('token') ||
            errorCode.toLowerCase().includes('auth')) {
          authLogger.warn(`${logContext} Potential auth error detected, forcing reauthentication...`);
          await forceReauthentication();
          return true;
        }
      }

      return false;
    } catch (handlerError) {
      authLogger.error(`${logContext} Error in auth error handler:`, handlerError);
      return false;
    }
  }, [forceReauthentication]);

  /**
   * Force reauthentication manually
   */
  const forceReauth = useCallback(async () => {
    authLogger.warn('Manual force reauthentication triggered');
    await forceReauthentication();
  }, [forceReauthentication]);

  /**
   * Immediate force login without cleanup - faster but less thorough
   */
  const immediateReauth = useCallback(async () => {
    authLogger.warn('Immediate force login triggered');
    await immediateForceLogin();
  }, [immediateForceLogin]);

  return {
    handleError,
    forceReauth,
    immediateReauth
  };
};
